import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Capacitor } from '@capacitor/core';
import { AuthContext } from '../Provider';
import {
  needsConsentRefresh,
  acceptAllCookies,
  rejectOptionalCookies
} from '../../utils/cookieConsentManager';
import CookiePreferencesModal from './CookiePreferencesModal';
import './CookieBanner.css';

/**
 * Cookie Consent Banner Component
 *
 * GDPR-compliant cookie consent banner that appears at bottom of screen.
 * Shows on first visit and every 6 months for consent refresh.
 *
 * Features:
 * - 3-second delay before showing (non-intrusive UX)
 * - Hidden on /cookies and /privacy pages
 * - 4 action buttons: Accept All, Reject Optional, Customize, Learn More
 * - Smooth animation with Framer Motion
 * - Syncs consent to database for logged-in users
 */
const CookieBanner = () => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [showBanner, setShowBanner] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [delayPassed, setDelayPassed] = useState(false);

  // Check if banner should be shown
  const shouldShowBanner = useMemo(() => {
    // Never show in the native app — cookie-consent banners are a web/GDPR
    // convention; native apps disclose data use via the app store instead.
    if (typeof Capacitor?.isNativePlatform === 'function' && Capacitor.isNativePlatform()) {
      return false;
    }

    // Don't show on cookie policy or privacy pages
    const hiddenPaths = ['/cookies', '/privacy'];
    if (hiddenPaths.includes(location.pathname)) {
      return false;
    }

    // Check if consent needs refresh
    return needsConsentRefresh();
  }, [location.pathname]);

  // 3-second delay before showing banner (non-intrusive UX)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDelayPassed(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Show banner after delay if needed
  useEffect(() => {
    if (delayPassed && shouldShowBanner) {
      setShowBanner(true);
    }
  }, [delayPassed, shouldShowBanner]);

  // Hide banner if navigating to excluded pages
  useEffect(() => {
    if (!shouldShowBanner && showBanner) {
      setShowBanner(false);
    }
  }, [shouldShowBanner, showBanner]);

  /**
   * Handle "Accept All" action
   * Enables all cookie categories and hides banner
   */
  const handleAcceptAll = async () => {
    try {
      await acceptAllCookies(user);
      setShowBanner(false);
      console.log('User accepted all cookies');
    } catch (error) {
      console.error('Error accepting all cookies:', error);
    }
  };

  /**
   * Handle "Reject Optional" action
   * Keeps only essential cookies and hides banner
   */
  const handleRejectOptional = async () => {
    try {
      await rejectOptionalCookies(user);
      setShowBanner(false);
      console.log('User rejected optional cookies');
    } catch (error) {
      console.error('Error rejecting optional cookies:', error);
    }
  };

  /**
   * Handle "Customize" action
   * Opens preferences modal for granular control
   */
  const handleCustomize = () => {
    setShowPreferencesModal(true);
  };

  /**
   * Handle "Learn More" action
   * Navigates to full cookie policy page
   */
  const handleLearnMore = () => {
    navigate('/cookies');
  };

  /**
   * Handle modal close
   * Hide banner if user made a choice in modal
   */
  const handleModalClose = (consentGiven) => {
    setShowPreferencesModal(false);
    if (consentGiven) {
      setShowBanner(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <motion.div
            className="cookie-banner"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <div className="cookie-banner-container">
              <div className="cookie-banner-content">
                <div className="cookie-banner-icon" role="img" aria-label="Cookie">
                  🍪
                </div>
                <div className="cookie-banner-text">
                  <h3 className="cookie-banner-title">
                    {t('cookieBanner.title')}
                  </h3>
                  <p className="cookie-banner-description">
                    {t('cookieBanner.description')}
                  </p>
                </div>
              </div>

              <div className="cookie-banner-actions">
                <button
                  className="cookie-banner-btn cookie-banner-btn-secondary"
                  onClick={handleLearnMore}
                  aria-label={t('cookieBanner.learnMore')}
                >
                  {t('cookieBanner.learnMore')}
                </button>

                <button
                  className="cookie-banner-btn cookie-banner-btn-secondary"
                  onClick={handleRejectOptional}
                  aria-label={t('cookieBanner.rejectOptional')}
                >
                  {t('cookieBanner.rejectOptional')}
                </button>

                <button
                  className="cookie-banner-btn cookie-banner-btn-secondary"
                  onClick={handleCustomize}
                  aria-label={t('cookieBanner.customize')}
                >
                  {t('cookieBanner.customize')}
                </button>

                <button
                  className="cookie-banner-btn cookie-banner-btn-primary"
                  onClick={handleAcceptAll}
                  aria-label={t('cookieBanner.acceptAll')}
                >
                  {t('cookieBanner.acceptAll')}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cookie Preferences Modal */}
      {showPreferencesModal && (
        <CookiePreferencesModal
          isOpen={showPreferencesModal}
          onClose={handleModalClose}
        />
      )}
    </>
  );
};

export default CookieBanner;
